import {
    DELIVERY_CALENDAR_THEME,
    addMonths,
    addWeeks,
    dateKeyFromDate,
    deliveryCadenceLabel,
    deliveryCalendarLabel,
    deliveryTargetLabel,
    formatDeliveryDate,
    formatDeliveryTime,
} from "./deliveryCalendar";
import {
    deliveryAddButtonStyle,
    deliveryCalendarCountStyle,
    deliveryCalendarTitleStyle,
    deliveryCalendarViewButtonStyle,
    deliveryCalendarViewGroupStyle,
    deliveryDayCountStyle,
    deliveryDayNumberStyle,
    deliveryDayStyle,
    deliveryEmptyDayButtonStyle,
    deliveryNavigationButtonStyle,
    deliveryNavigationStyle,
    deliveryOccurrenceCadenceStyle,
    deliveryOccurrenceDriverStyle,
    deliveryOccurrenceLabelStyle,
    deliveryOccurrenceMovedStyle,
    deliveryOccurrenceStyle,
    deliveryOccurrenceTargetsStyle,
    deliveryOccurrenceTimeStyle,
    deliveryWeekDayStyle,
    deliveryWeekDaysStyle,
} from "./deliveryCalendarStyles";

const WEEK_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Titre, nombre de livraisons visibles, choix de la vue et bouton d'ajout.
export function DeliveryCalendarHeader({ loading, visibleDeliveryCount, calendarView, setCalendarView, onAdd }) {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", padding: "22px 28px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <h2 style={deliveryCalendarTitleStyle}>Calendrier des livraisons</h2>
                {!loading && <span style={deliveryCalendarCountStyle}>{visibleDeliveryCount}</span>}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <div role="group" aria-label="Vue calendrier livraison" style={deliveryCalendarViewGroupStyle}>
                    <button type="button" aria-pressed={calendarView === "month"} onClick={() => setCalendarView("month")} style={deliveryCalendarViewButtonStyle(calendarView === "month")}>Mensuel</button>
                    <button type="button" aria-pressed={calendarView === "week"} onClick={() => setCalendarView("week")} style={deliveryCalendarViewButtonStyle(calendarView === "week")}>Hebdomadaire</button>
                </div>
                <button type="button" onClick={onAdd} style={deliveryAddButtonStyle}>
                    Ajouter une livraison
                </button>
            </div>
        </div>
    );
}

// Période précédente / suivante (mois ou semaine selon la vue) et retour à aujourd'hui.
export function DeliveryCalendarNavigation({ calendarMonth, calendarView, setCalendarMonth }) {
    const shift = amount => setCalendarMonth(current => calendarView === "week" ? addWeeks(current, amount) : addMonths(current, amount));
    return (
        <div style={deliveryNavigationStyle}>
            <div style={{ display: "inline-flex", gap: "10px", alignItems: "center" }}>
                <button type="button" onClick={() => shift(-1)} style={deliveryNavigationButtonStyle}>Précédent</button>
                <button type="button" onClick={() => setCalendarMonth(new Date())} style={deliveryNavigationButtonStyle}>Aujourd'hui</button>
                <button type="button" onClick={() => shift(1)} style={deliveryNavigationButtonStyle}>Suivant</button>
            </div>
            <div style={{ color: DELIVERY_CALENDAR_THEME.ink, fontSize: "22px", fontWeight: 800, textTransform: "capitalize" }}>
                {deliveryCalendarLabel(calendarMonth, calendarView)}
            </div>
        </div>
    );
}

// Occurrence d'une livraison dans une case du calendrier : cliquer l'ouvre, la glisser la déplace.
function DeliveryOccurrence({ planning, drag, onEdit }) {
    const moving = drag.movingOccurrenceKey === `${planning.id}-${planning.occurrenceDate}`;
    return (
        <button
            type="button"
            draggable
            onDragStart={event => drag.handleDeliveryDragStart(event, planning)}
            onDragEnd={drag.handleDeliveryDragEnd}
            onClick={() => onEdit(planning)}
            style={deliveryOccurrenceStyle(moving)}
        >
            <span style={deliveryOccurrenceTimeStyle}>
                {formatDeliveryTime(planning.deliveryTime)}
            </span>
            <span style={deliveryOccurrenceLabelStyle}>
                {planning.label}
            </span>
            <span style={deliveryOccurrenceTargetsStyle}>
                {(planning.ehpads ?? []).map(deliveryTargetLabel).join(", ")}
            </span>
            {(planning.driverName || planning.driverEmail) && (
                <span style={deliveryOccurrenceDriverStyle}>
                    {planning.driverName || planning.driverEmail}
                </span>
            )}
            {planning.frequency !== "PONCTUELLE" && (
                <span style={deliveryOccurrenceCadenceStyle}>
                    {deliveryCadenceLabel(planning)}
                </span>
            )}
            {planning.moved && (
                <span style={deliveryOccurrenceMovedStyle}>
                    Déplacée du {formatDeliveryDate(planning.occurrenceDate)}
                </span>
            )}
        </button>
    );
}

// Case d'un jour : numéro (ajout d'une livraison ce jour-là), occurrences, et zone de dépôt du glisser-déposer.
function DeliveryDay({ day, dayPlannings, calendarView, drag, onAdd, onEdit }) {
    const hasPlannings = dayPlannings.length > 0;
    const isToday = day.key === dateKeyFromDate(new Date());
    const canAddOnDay = calendarView === "week" || day.inMonth;
    return (
        <div
            data-testid={`delivery-day-${day.key}`}
            onDragOver={event => {
                if (canAddOnDay) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                }
            }}
            onDrop={event => drag.handleDeliveryDrop(event, day.key, canAddOnDay)}
            style={deliveryDayStyle(day, calendarView, canAddOnDay)}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
                <button
                    type="button"
                    onClick={() => onAdd(day.key)}
                    disabled={!canAddOnDay}
                    title={canAddOnDay ? `Ajouter une livraison le ${formatDeliveryDate(day.key)}` : ""}
                    style={deliveryDayNumberStyle(isToday, canAddOnDay)}
                >
                    {day.date.getDate()}
                </button>
                {hasPlannings && (
                    <span style={deliveryDayCountStyle}>
                        {dayPlannings.length}
                    </span>
                )}
            </div>
            <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
                {dayPlannings.map(planning => (
                    <DeliveryOccurrence key={`${planning.id}-${planning.occurrenceDate}`} planning={planning} drag={drag} onEdit={onEdit} />
                ))}
                {!hasPlannings && canAddOnDay && (
                    <button type="button" onClick={() => onAdd(day.key)} style={deliveryEmptyDayButtonStyle(calendarView)}>
                        +
                    </button>
                )}
                {!hasPlannings && !canAddOnDay && (
                    <div style={{ minHeight: "58px" }} />
                )}
            </div>
        </div>
    );
}

// Grille du mois ou de la semaine, du lundi au dimanche.
export function DeliveryCalendarGrid({ calendarDays, planningsByDate, calendarView, drag, onAdd, onEdit }) {
    return (
        <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: "820px", overflow: "hidden", background: "#fff", borderTop: `1px solid ${DELIVERY_CALENDAR_THEME.border}` }}>
                <div style={deliveryWeekDaysStyle}>
                    {WEEK_DAYS.map(day => (
                        <div key={day} style={deliveryWeekDayStyle}>
                            {day}
                        </div>
                    ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                    {calendarDays.map(day => (
                        <DeliveryDay
                            key={day.key}
                            day={day}
                            dayPlannings={planningsByDate[day.key] || []}
                            calendarView={calendarView}
                            drag={drag}
                            onAdd={onAdd}
                            onEdit={onEdit}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
